import { ComponentStyleProps } from "../../../../lib/styles/props/component-style-props";
import {
  Box,
  Button,
  ComponentMultiStyleConfig,
  Flex,
  forwardRef,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  Tooltip,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { ReactElement, ReactNode, useMemo } from "react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { hasValue, isNullish } from "../../../../lib/util/common-util";

export type CMButtonSearchConditionProps = {
  label: string;

  /**
   * 表示値。
   */
  displayValue?: string | string[] | null;

  /**
   * ボタン右端のアイコン
   */
  rightIcon?: ReactElement;

  /**
   * 検索条件選択用の別コンポーネントを受け取り、条件として表示する。
   */
  children?: ReactNode;

  /**
   * ポップアップの開閉状態。
   */
  isOpenPopup: boolean;

  onClick?: () => void;

  usePortal?: boolean;
} & ComponentStyleProps;

/**
 * 検索条件ボタンのコンポーネント.
 *
 * ボタン押下で検索条件が表示される
 */
export const CMButtonSearchCondition = forwardRef<
  CMButtonSearchConditionProps,
  "button"
>(
  (
    {
      label,
      displayValue,
      rightIcon = <ChevronDownIcon />,
      children,

      isOpenPopup,

      onClick,

      usePortal = false,

      sx,
      ...rest
    }: CMButtonSearchConditionProps,
    ref,
  ) => {
    const styles = useMultiStyleConfig("CMButtonSearchConditionStyles", {});

    const _displayValue = useMemo(() => {
      if (isNullish(displayValue)) return null;
      if (typeof displayValue === "string") return displayValue;
      return displayValue?.join(", ");
    }, [displayValue]);

    const popoverContent = (
      // <PopoverContent ref={containerRef} sx={styles.popoverContent}>
      <PopoverContent sx={styles.popoverContent}>
        <PopoverBody sx={styles.popoverBody}>{children}</PopoverBody>
      </PopoverContent>
    );

    return (
      <Box sx={{ ...styles.container, ...sx }} {...rest} ref={ref}>
        <Popover isOpen={isOpenPopup} placement="bottom-start">
          <PopoverTrigger>
            <Button rightIcon={rightIcon} onClick={onClick} sx={styles.button}>
              <Flex sx={styles.buttonLabelWrap}>
                {hasValue(_displayValue) ? (
                  <>
                    <Text sx={styles.label}>{label}</Text>
                    <Tooltip
                      label={
                        _displayValue.length > 0 ? _displayValue : undefined
                      }
                      aria-label={"selected value"}
                    >
                      <Text sx={styles.value}>{_displayValue}</Text>
                    </Tooltip>
                  </>
                ) : (
                  <Text sx={styles.onlyLabel}>{label}</Text>
                )}
              </Flex>
            </Button>
          </PopoverTrigger>
          {usePortal ? <Portal>{popoverContent}</Portal> : popoverContent}
        </Popover>
      </Box>
    );
  },
);
export const CMButtonSearchConditionStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "button",
    "buttonLabelWrap",
    "onlyLabel",
    "label",
    "value",
    "popoverContent",
    "popoverBody",
  ],
  baseStyle: {
    container: {
      // alignItems: "stretch",
      h: "42px",
      w: "180px",
    },
    button: {
      w: "100%",
      h: "100%",
      bg: "white",
      border: "solid 1px",
      borderColor: "gray.200",
      borderRadius: "6px",
      justifyContent: "space-between",
    },
    buttonLabelWrap: {
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "center",
      overflow: "hidden",
    },
    onlyLabel: {
      fontWeight: "400",
      fontSize: "14px",
      lineHeight: "24px",
      color: "gray.700",
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
    label: {
      fontSize: "11px",
      lineHeight: "16px",
      color: "gray.500",
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
    value: {
      alignSelf: "stretch",
      flex: "1",

      textAlign: "start",

      fontSize: "14px",
      color: "blue.700",
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
    popoverContent: {
      display: "flex",
      flexFlow: "row nowrap",
      alignItems: "flex-start",
      w: "max-content",
    },
    popoverBody: {
      flex: 1,
      w: "max-content",
      px: "8px",
      py: "8px",
    },
  },
};
